import clsx from 'clsx';
import { useAtomValue, useSetAtom } from 'jotai';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// Types
import type { DatepickerPeriodsAvailableType } from '@/lib/periods';
// Lib
import { periodsTranslation } from '@/lib/periods';

// Atoms
import { datepickerAtom } from '@/components/base/Datepicker/DatepickerComponent/Atoms/datepickerAtom';
import {
    getPeriodsInfosAtom,
    updatePeriodAtom
} from '@/components/base/Datepicker/DatepickerComponent/Atoms/periodsAtom';
// Components
import ActionTag from '@/components/base/Tag/ActionTag';
import Tooltip from '@/components/base/Tooltip';

type DatepickerPeriodsProps = {
    periods: DatepickerPeriodsAvailableType[];
};

export function DatepickerPeriods({ periods }: DatepickerPeriodsProps) {
    const { t } = useTranslation();
    const datepickerData = useAtomValue(datepickerAtom);
    const updatePeriod = useSetAtom(updatePeriodAtom);
    const periodsInfos = useAtomValue(getPeriodsInfosAtom);

    if (datepickerData.mode === 'single') return null;

    return (
        <div>
            <p className="mb-2 text-8 uppercase leading-[normal] tracking-0.5 text-icoGray-500 dark:text-icoDarkMode-wolf">
                {t('commons:datepicker.predefined_periods')}
            </p>
            <div className="flex flex-wrap items-center gap-1">
                {periods.map((period) => {
                    const isSelected = datepickerData.period === period;
                    const isDisabled = periodsInfos[period].isDisabled;
                    let variant: React.ComponentProps<typeof ActionTag>['variant'] =
                        'option_transparent';

                    if (isDisabled) {
                        variant = 'option_disabled';
                    } else if (isSelected) {
                        variant = 'option_blue';
                    }

                    return (
                        <Tooltip
                            key={period}
                            position="top"
                            content={isDisabled ? t('commons:datepicker.no_data_period') : null}>
                            <span>
                                <button
                                    type="button"
                                    disabled={isDisabled}
                                    data-testid={
                                        isSelected
                                            ? `datepicker-period-${period}-selected`
                                            : `datepicker-period-${period}`
                                    }
                                    className="rounded-full focus:ring-0"
                                    onClick={() => updatePeriod(period)}>
                                    <ActionTag variant={variant}>
                                        <span
                                            className={clsx('text-xxs leading-[normal]', {
                                                'text-icoBlack dark:text-white':
                                                    !isSelected && !isDisabled
                                            })}>
                                            {t(periodsTranslation[period])}
                                        </span>
                                    </ActionTag>
                                </button>
                            </span>
                        </Tooltip>
                    );
                })}
            </div>
        </div>
    );
}
